/**
 * Descriptive building blocks shared by the statistics, chart and grading tools.
 *
 * Pure functions over plain arrays. Callers parse input with `@/lib/parse-number`
 * (which reads a decimal comma in the locales that write one) and pass numbers here.
 */

export function sum(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    total += value;
  }
  return total;
}

export function mean(values: readonly number[]): number {
  return values.length === 0 ? Number.NaN : sum(values) / values.length;
}

/** Sum of squared deviations from the mean. */
export function sumOfSquares(values: readonly number[]): number {
  const m = mean(values);
  let total = 0;
  for (const value of values) {
    total += (value - m) ** 2;
  }
  return total;
}

/** Sample variance (n − 1) by default; pass `population` for n. */
export function variance(
  values: readonly number[],
  population = false
): number {
  const n = values.length;
  if (n < (population ? 1 : 2)) {
    return Number.NaN;
  }
  return sumOfSquares(values) / (population ? n : n - 1);
}

export function standardDeviation(
  values: readonly number[],
  population = false
): number {
  return Math.sqrt(variance(values, population));
}

export function sorted(values: readonly number[]): number[] {
  return [...values].sort((a, b) => a - b);
}

export function median(values: readonly number[]): number {
  return quantile(values, 0.5, "inclusive");
}

/**
 * How a quantile between two data points is chosen.
 *
 * Textbooks, calculators and software disagree, and a student checking homework needs
 * the one their course uses:
 * - `inclusive` — Hyndman–Fan type 7: Excel `QUARTILE.INC`, R's default, NumPy.
 * - `exclusive` — type 6: Excel `QUARTILE.EXC`, SPSS, Minitab.
 * - `tukey` — the median of each half, excluding the overall median when n is odd: the
 *   method most introductory courses and the TI-83/84 use for Q1 and Q3. It is only
 *   defined for p = 0.25, 0.5 and 0.75; other values fall back to `inclusive`.
 */
export type QuantileMethod = "inclusive" | "exclusive" | "tukey";

function interpolate(data: number[], position: number): number {
  const lower = Math.floor(position);
  const fraction = position - lower;
  if (lower + 1 >= data.length) {
    return data.at(-1) as number;
  }
  if (lower < 0) {
    return data[0];
  }
  return data[lower] + fraction * (data[lower + 1] - data[lower]);
}

function medianOfSorted(data: number[]): number {
  const n = data.length;
  if (n === 0) {
    return Number.NaN;
  }
  const middle = Math.floor(n / 2);
  return n % 2 === 0 ? (data[middle - 1] + data[middle]) / 2 : data[middle];
}

export function quantile(
  values: readonly number[],
  p: number,
  method: QuantileMethod = "inclusive"
): number {
  const data = sorted(values);
  const n = data.length;
  if (n === 0) {
    return Number.NaN;
  }
  if (method === "tukey" && (p === 0.25 || p === 0.75)) {
    const half = Math.floor(n / 2);
    const lowerHalf = data.slice(0, half);
    const upperHalf = data.slice(n % 2 === 0 ? half : half + 1);
    if (lowerHalf.length === 0) {
      return data[0];
    }
    return medianOfSorted(p === 0.25 ? lowerHalf : upperHalf);
  }
  if (method === "exclusive") {
    // Type 6: position (n + 1)p, clamped to the data.
    return interpolate(data, (n + 1) * p - 1);
  }
  // Type 7: position (n − 1)p.
  return interpolate(data, (n - 1) * p);
}

export interface FiveNumberSummary {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  iqr: number;
  /** Tukey's fences: Q1 − 1.5 × IQR and Q3 + 1.5 × IQR. */
  lowerFence: number;
  upperFence: number;
  outliers: number[];
}

export function fiveNumberSummary(
  values: readonly number[],
  method: QuantileMethod = "tukey"
): FiveNumberSummary {
  const data = sorted(values);
  const q1 = quantile(data, 0.25, method);
  const q3 = quantile(data, 0.75, method);
  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;
  return {
    min: data[0],
    q1,
    median: medianOfSorted(data),
    q3,
    max: data.at(-1) as number,
    iqr,
    lowerFence,
    upperFence,
    outliers: data.filter((value) => value < lowerFence || value > upperFence),
  };
}

/**
 * Ranks with ties given their average rank (1-based) — the convention every rank test
 * here uses: Spearman, Mann–Whitney, Wilcoxon and Kruskal–Wallis.
 */
export function averageRanks(values: readonly number[]): number[] {
  const order = values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);
  const ranks = new Array<number>(values.length);
  let i = 0;
  while (i < order.length) {
    let j = i;
    while (j + 1 < order.length && order[j + 1].value === order[i].value) {
      j += 1;
    }
    const rank = (i + j + 2) / 2;
    for (let k = i; k <= j; k++) {
      ranks[order[k].index] = rank;
    }
    i = j + 1;
  }
  return ranks;
}

/** Sizes of each group of tied values, for tie corrections. */
export function tieGroupSizes(values: readonly number[]): number[] {
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.values()].filter((count) => count > 1);
}

/** Pearson's r for paired data. NaN when either variable has no spread. */
export function pearson(x: readonly number[], y: readonly number[]): number {
  const n = Math.min(x.length, y.length);
  if (n < 2) {
    return Number.NaN;
  }
  const mx = mean(x.slice(0, n));
  const my = mean(y.slice(0, n));
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx;
    const dy = y[i] - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx === 0 || syy === 0) {
    return Number.NaN;
  }
  return sxy / Math.sqrt(sxx * syy);
}

/** Sample covariance (n − 1) by default; pass `population` for n. */
export function covariance(
  x: readonly number[],
  y: readonly number[],
  population = false
): number {
  const n = Math.min(x.length, y.length);
  if (n < (population ? 1 : 2)) {
    return Number.NaN;
  }
  const mx = mean(x.slice(0, n));
  const my = mean(y.slice(0, n));
  let total = 0;
  for (let i = 0; i < n; i++) {
    total += (x[i] - mx) * (y[i] - my);
  }
  return total / (population ? n : n - 1);
}
